import { useMemo, useState } from "react";
import { Zap, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId, QUOTE_EVENT } from "@/lib/api";

/* Base ampacity (A): Cu, XLPE 90°C, clipped direct, 30°C ambient — indicative values
   aligned to IEC 60364-5-52 / IS 3961 tables, rounded conservatively. */
const AMPACITY_CU = { 1.5: 18, 2.5: 24, 4: 32, 6: 41, 10: 57, 16: 76, 25: 101, 35: 125, 50: 151, 70: 192, 95: 232, 120: 269, 150: 309, 185: 353, 240: 415, 300: 473 };
const SIZES = Object.keys(AMPACITY_CU).map(Number);
const METHOD_FACTOR = { clipped: 1.0, conduit: 0.8, buried: 0.9 };
const TEMP_FACTOR = { 30: 1.0, 35: 0.96, 40: 0.91, 45: 0.87, 50: 0.82 };
const AL_FACTOR = 0.78; /* Al carries ~78% of Cu for same size */

function sizeCable(load, phase, len, mat, method, temp, pf) {
    const V = phase === "1ph" ? 230 : 415;
    const I = phase === "1ph" ? (load * 1000) / (V * pf) : (load * 1000) / (Math.sqrt(3) * V * pf);
    const rho = mat === "Aluminium" ? 0.0283 : 0.0175;
    const derate = METHOD_FACTOR[method] * TEMP_FACTOR[temp] * (mat === "Aluminium" ? AL_FACTOR : 1);
    for (const s of SIZES) {
        const amp = AMPACITY_CU[s] * derate;
        const R = (rho * 1000) / s;
        const vd =
            phase === "1ph"
                ? ((2 * I * (len / 1000) * R) / V) * 100
                : ((Math.sqrt(3) * I * (len / 1000) * R) / V) * 100;
        if (amp >= I && vd <= 5) return { size: s, amps: I, vd, ampacity: amp };
    }
    return { size: null, amps: I, vd: null, ampacity: null };
}

const inputCls =
    "w-full bg-white border border-line rounded-md px-4 py-3 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5";

export default function CableCalculator() {
    const [load, setLoad] = useState("15");
    const [phase, setPhase] = useState("3ph");
    const [len, setLen] = useState("50");
    const [mat, setMat] = useState("Copper");
    const [method, setMethod] = useState("clipped");
    const [temp, setTemp] = useState("40");
    const [pf, setPf] = useState("0.85");

    const res = useMemo(() => {
        const l = parseFloat(load);
        const d = parseFloat(len);
        const p = parseFloat(pf);
        if (!l || l <= 0 || !d || d <= 0 || !p || p <= 0 || p > 1) return null;
        return sizeCable(l, phase, d, mat, method, Number(temp), p);
    }, [load, phase, len, mat, method, temp, pf]);

    const methodLabel = { clipped: "clipped direct", conduit: "in conduit", buried: "buried direct" }[method];

    const discuss = () => {
        if (!res) return;
        const msg = res.size
            ? `Cable sizing: ${load} kW, ${phase === "1ph" ? "1-Phase 230V" : "3-Phase 415V"}, ${len} m run, ${mat} conductor, ${methodLabel}, ${temp}°C ambient, PF ${pf} → indicative ${res.size} sqmm (${res.amps.toFixed(1)} A, ${res.vd.toFixed(2)}% drop). Please quote.`
            : `Cable sizing: ${load} kW, ${phase === "1ph" ? "1-Phase 230V" : "3-Phase 415V"}, ${len} m run, ${mat}, ${methodLabel}, ${temp}°C, PF ${pf} → heavy load, need engineer advice.`;
        window.dispatchEvent(new CustomEvent(QUOTE_EVENT, { detail: { interest: "General Enquiry", message: msg } }));
        scrollToId("enquiry");
    };

    return (
        <section id="calculator" className="relative hud-bg py-20 lg:py-28 border-y border-line" data-testid="calculator-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <Reveal className="mb-12 max-w-2xl">
                    <Eyebrow className="mb-5">06 — Tools</Eyebrow>
                    <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink mb-4">
                        Size your cable <em className="text-copper">in seconds.</em>
                    </h2>
                    <p className="text-mist leading-relaxed">
                        Load, run length, installation method and site conditions in — we work out the
                        current, apply standard derating, check the voltage drop, and suggest the nearest
                        standard size.
                    </p>
                </Reveal>

                <div className="grid lg:grid-cols-2 gap-6 items-stretch">
                    <Reveal>
                        <div className="rounded-md border border-line bg-white p-6 md:p-8 shadow-sm grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className={labelCls}>Load (kW)</label>
                                <input data-testid="calculator-load-input" type="number" min="0" value={load} onChange={(e) => setLoad(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Phase</label>
                                <select data-testid="calculator-phase-select" value={phase} onChange={(e) => setPhase(e.target.value)} className={`${inputCls} appearance-none`}>
                                    <option value="3ph">3-Phase · 415 V</option>
                                    <option value="1ph">1-Phase · 230 V</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Run Length (m)</label>
                                <input data-testid="calculator-length-input" type="number" min="0" value={len} onChange={(e) => setLen(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Conductor</label>
                                <select data-testid="calculator-material-select" value={mat} onChange={(e) => setMat(e.target.value)} className={`${inputCls} appearance-none`}>
                                    <option>Copper</option>
                                    <option>Aluminium</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Installation</label>
                                <select data-testid="calculator-method-select" value={method} onChange={(e) => setMethod(e.target.value)} className={`${inputCls} appearance-none`}>
                                    <option value="clipped">Clipped direct / on tray</option>
                                    <option value="conduit">In conduit / trunking</option>
                                    <option value="buried">Buried direct in ground</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Ambient Temp</label>
                                <select data-testid="calculator-temp-select" value={temp} onChange={(e) => setTemp(e.target.value)} className={`${inputCls} appearance-none`}>
                                    <option value="30">30 °C</option>
                                    <option value="35">35 °C</option>
                                    <option value="40">40 °C (typical)</option>
                                    <option value="45">45 °C</option>
                                    <option value="50">50 °C</option>
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelCls}>Power Factor (0.80 – 1.00)</label>
                                <input data-testid="calculator-pf-input" type="number" min="0.8" max="1" step="0.01" value={pf} onChange={(e) => setPf(e.target.value)} className={inputCls} />
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="rounded-md bg-inkdark text-white p-6 md:p-8 shadow-[0_24px_60px_rgba(24,23,22,0.25)] h-full flex flex-col justify-between min-h-[280px]">
                            {res ? (
                                <>
                                    <div>
                                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-copper-bright mb-3">
                                            Indicative Result
                                        </p>
                                        {res.size ? (
                                            <p className="font-display font-bold text-5xl md:text-6xl leading-none" data-testid="calculator-result-sqmm">
                                                {res.size} <span className="text-2xl text-white/60">sqmm</span>
                                            </p>
                                        ) : (
                                            <p className="font-display font-bold text-3xl md:text-4xl leading-tight" data-testid="calculator-result-sqmm">
                                                Parallel runs — talk to us
                                            </p>
                                        )}
                                        <div className="mt-6 space-y-2 font-mono text-sm">
                                            <p className="flex justify-between border-b border-white/10 pb-2">
                                                <span className="text-white/50">FULL-LOAD CURRENT</span>
                                                <span data-testid="calculator-result-amps" className="text-copper-bright">{res.amps.toFixed(1)} A</span>
                                            </p>
                                            <p className="flex justify-between border-b border-white/10 pb-2">
                                                <span className="text-white/50">VOLTAGE DROP</span>
                                                <span data-testid="calculator-result-vdrop" className="text-copper-bright">
                                                    {res.vd !== null ? `${res.vd.toFixed(2)} %` : "—"}
                                                </span>
                                            </p>
                                            <p className="flex justify-between border-b border-white/10 pb-2">
                                                <span className="text-white/50">DERATED AMPACITY</span>
                                                <span data-testid="calculator-result-ampacity" className="text-copper-bright">
                                                    {res.ampacity !== null ? `${res.ampacity.toFixed(0)} A` : "—"}
                                                </span>
                                            </p>
                                            <p className="flex justify-between">
                                                <span className="text-white/50">CONDITIONS</span>
                                                <span className="text-white/70">{methodLabel} · {temp}°C · ≤5% drop</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <div className="rounded-md border border-white/10 bg-white/5 p-4 mb-5" data-testid="calculator-assumptions">
                                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-2.5">Assumptions used</p>
                                            <ul className="space-y-1 font-mono text-[11px] text-white/60">
                                                <li data-testid="assumption-method">Installation ({methodLabel}): ×{METHOD_FACTOR[method].toFixed(2)}</li>
                                                <li data-testid="assumption-temp">Ambient {temp}°C: ×{TEMP_FACTOR[temp].toFixed(2)}</li>
                                                {mat === "Aluminium" && <li data-testid="assumption-al">Aluminium conductor: ×{AL_FACTOR}</li>}
                                                <li data-testid="assumption-vdrop">Voltage drop limit: 5%</li>
                                                <li data-testid="assumption-pf">Power factor: {pf}</li>
                                                <li data-testid="assumption-standard">Reference: IS 3961 / IEC 60364-5-52</li>
                                            </ul>
                                        </div>
                                        <button
                                            data-testid="calculator-add-to-quote-btn"
                                            onClick={discuss}
                                            className="w-full rounded-full bg-copper-bright text-white font-mono uppercase tracking-[0.18em] text-[11px] py-3.5 hover:bg-copper transition-colors duration-300 flex items-center justify-center gap-2"
                                        >
                                            Get a quote for this sizing <ArrowRight size={14} />
                                        </button>
                                        <p className="mt-3 font-mono text-[9px] tracking-[0.15em] uppercase text-white/40 flex items-center gap-2">
                                            <Zap size={10} className="text-copper-bright" />
                                            Result is indicative — final cable size is confirmed by our engineers
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-col items-start justify-center h-full">
                                    <Zap size={22} className="text-copper-bright mb-3" />
                                    <p className="text-white/60 text-sm">Enter a load and run length to size the cable.</p>
                                </div>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
